import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { ApiError } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { showApiError } from "@/lib/errors";
import { getAiAnalyticsUrl, getAiAnalyticsBaseUrl } from "@/lib/api/aiAnalyticsUrl";
import { getOrganizationId } from "@/lib/api/organization";
import { useBusinessAccountContext } from "@/hooks/useBusinessAccountContext";

const ADS_API = "/ads";

// === API Functions ===

const adsApi = {
  // Setup
  getSetupStatus: (businessAccountId?: string | null) =>
    apiClient.get(
      `${ADS_API}/setup/status${businessAccountId ? `?business_account_id=${encodeURIComponent(businessAccountId)}` : ""}`
    ),
  getOAuthUrl: () => apiClient.get(`${ADS_API}/setup/oauth-url`),
  handleCallback: (code: string) => apiClient.post(`${ADS_API}/setup/callback`, { code }),
  connectAdAccount: (data: any) => apiClient.post(`${ADS_API}/setup/connect`, data),
  getBalance: (businessAccountId?: string | null) =>
    apiClient.get(
      `${ADS_API}/setup/balance${businessAccountId ? `?business_account_id=${encodeURIComponent(businessAccountId)}` : ""}`
    ),
  getAvailableAdAccounts: (opts?: { includeBusinessScoped?: boolean }) =>
    apiClient.get(
      `${ADS_API}/setup/ad-accounts${
        opts?.includeBusinessScoped ? "?include_business_scoped=true" : ""
      }`
    ),
  switchAdAccount: (data: any) => apiClient.post(`${ADS_API}/setup/switch`, data),
  // `adAccountId` targets ONE account. Omitted → the active one (legacy behaviour).
  disconnect: (adAccountId?: string, businessAccountId?: string | null) => {
    const params = new URLSearchParams();
    if (adAccountId) params.set("ad_account_id", adAccountId);
    if (businessAccountId) params.set("business_account_id", businessAccountId);
    const qs = params.toString();
    return apiClient.delete(`${ADS_API}/setup/disconnect${qs ? `?${qs}` : ""}`);
  },

  // Campaigns
  getCampaigns: (params: any) => apiClient.get(`${ADS_API}/campaigns`, { params }),
  createCampaign: (data: any) => apiClient.post(`${ADS_API}/campaigns`, data),

  // Run As Ad (T1.1 / T1.2)
  createAdFromPost: (data: any) => apiClient.post(`${ADS_API}/from-post`, data),
  recommendAd: (data: any) => apiClient.post(`${ADS_API}/recommend`, data),

  // Revenue Attribution (T2.3 / T2.4)
  getAttribution: (params?: { from?: string; to?: string }) =>
    apiClient.get(`${ADS_API}/attribution`, params ? { params } : undefined),

  // CRM lead quality per ad (Phase 1) — stage-name buckets for Ads tab overlay
  getLeadQuality: (params?: { from?: string; to?: string }) =>
    apiClient.get(`${ADS_API}/lead-quality`, params ? { params } : undefined),

  // Per-stage CRM counts for one Meta ad (Ads-tab drawer)
  getLeadQualityStages: (params: {
    ad_id?: string;
    ad_name?: string;
    from?: string;
    to?: string;
  }) => apiClient.get(`${ADS_API}/lead-quality/stages`, { params }),

  // AI Suggest Ad (T3.3) — "should I promote this post?"
  suggestFromPost: (data: { post: Record<string, any>; baseline?: Record<string, any>; business_account_id?: string }) =>
    apiClient.post(`${ADS_API}/suggest-from-post`, data),
  // AI Suggest Ad (Phase 3) — pending suggestions list + dismiss
  getSuggestions: () => apiClient.get(`${ADS_API}/suggestions`),
  dismissSuggestion: (id: string) => apiClient.post(`${ADS_API}/suggestions/${id}/dismiss`),
  getCampaign: (id: string) => apiClient.get(`${ADS_API}/campaigns/${id}`),
  updateCampaign: (id: string, data: any) => apiClient.patch(`${ADS_API}/campaigns/${id}`, data),
  deleteCampaign: (id: string) => apiClient.delete(`${ADS_API}/campaigns/${id}`),
  syncCampaign: (id: string) => apiClient.post(`${ADS_API}/campaigns/${id}/sync`),
  duplicateCampaign: (id: string) => apiClient.post(`${ADS_API}/campaigns/${id}/duplicate`),

  // Insights
  getCampaignInsights: (id: string, params: any) =>
    apiClient.get(`${ADS_API}/campaigns/${id}/insights`, { params }),
  getLeadsChart: (id: string, params: any) =>
    apiClient.get(`${ADS_API}/campaigns/${id}/leads-chart`, { params }),

  // Search
  searchInterests: (query: string) => apiClient.post(`${ADS_API}/search/interests`, { query }),
  searchLocations: (query: string) => apiClient.post(`${ADS_API}/search/locations`, { query }),

  // Audiences
  getAudiences: () => apiClient.get(`${ADS_API}/audiences`),
  createCustomAudience: (data: any) => apiClient.post(`${ADS_API}/audiences/custom`, data),
  createAudienceFromContacts: (data: any) =>
    apiClient.post(`${ADS_API}/audiences/from-contacts`, data),
  createLookalikeAudience: (data: any) => apiClient.post(`${ADS_API}/audiences/lookalike`, data),
  getAudiencePresets: () => apiClient.get(`${ADS_API}/audiences/presets`),
  saveAudiencePreset: (data: any) => apiClient.post(`${ADS_API}/audiences/presets`, data),

  // Flows
  getFlows: () => apiClient.get(`${ADS_API}/flows`),

  // Fetch from Meta
  // `range` (YYYY-MM-DD since/until) is Meta's time_range and WINS over
  // date_preset server-side. Sent only when both bounds are present.
  fetchAdsFromMeta: (
    datePreset?: string,
    range?: { since: string; until: string }
  ) => {
    const params: Record<string, string> = {};
    if (datePreset) params.date_preset = datePreset;
    if (range?.since && range?.until) {
      params.since = range.since;
      params.until = range.until;
    }
    return apiClient.get(
      `${ADS_API}/fetch-from-meta`,
      Object.keys(params).length ? { params } : undefined
    );
  },
  getMultiAccountSummary: (adAccountIds: string[], datePreset?: string) =>
    apiClient.post(`${ADS_API}/multi-account-summary`, {
      ad_account_ids: adAccountIds,
      date_preset: datePreset,
    }),
  getBreakdown: (dimension: string, datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/breakdown`, {
      params: { dimension, ...(datePreset ? { date_preset: datePreset } : {}) },
    }),
  getFunnel: (datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/funnel`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  getAnomalies: () => apiClient.get(`${ADS_API}/insights/anomalies`),
  getWeeklyDigest: () => apiClient.get(`${ADS_API}/insights/weekly-digest`),
  buildCustomReport: (body: {
    level: string; metrics: string[]; sortBy?: string; sortDir?: string; date_preset?: string;
  }) => apiClient.post(`${ADS_API}/insights/custom-report`, body),
  getCreativeInsights: (datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/creative`, datePreset ? { params: { date_preset: datePreset } } : undefined),
  getSpendPacing: () => apiClient.get(`${ADS_API}/insights/pacing`),
  getRevenuePerLead: (datePreset?: string) =>
    apiClient.get(`${ADS_API}/insights/revenue-per-lead`, datePreset ? { params: { date_preset: datePreset } } : undefined),

  // Meta ad actions
  updateMetaAd: (metaAdId: string, data: any) => apiClient.post(`${ADS_API}/meta-ads/${metaAdId}`, data),
  updateMetaCampaign: (metaCampaignId: string, data: any) => apiClient.post(`${ADS_API}/meta-campaigns/${metaCampaignId}`, data),

  // Image upload
  uploadImage: (imageUrl: string) => apiClient.post(`${ADS_API}/upload-image`, { image_url: imageUrl }),
  uploadImageFile: async (formData: FormData) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${baseUrl}${ADS_API}/upload-image-file`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) throw ApiError.fromResult(json, `Upload failed (${res.status})`);
    return { data: json.data, error: null, success: true };
  },
  uploadVideoFile: async (formData: FormData) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api/v1";
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${baseUrl}${ADS_API}/upload-video-file`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) throw ApiError.fromResult(json, `Video upload failed (${res.status})`);
    return { data: json.data, error: null, success: true };
  },

  // AI generation
  generateAdCopy: (data: any) => apiClient.post(`${ADS_API}/generate-copy`, data),

  // AdSet actions
  updateMetaAdSet: (metaAdSetId: string, data: any) => apiClient.post(`${ADS_API}/meta-adsets/${metaAdSetId}`, data),

  // Single ad detail (fresh from Meta)
  getMetaAdDetail: (metaAdId: string) => apiClient.get(`${ADS_API}/meta-ads/${metaAdId}`),

  // Campaign detail (drill-down)
  getMetaCampaignDetail: (metaCampaignId: string) =>
    apiClient.get(`${ADS_API}/meta-campaigns/${metaCampaignId}/detail`),

  // Ad Set detail (drill-down)
  getMetaAdSetDetail: (metaAdSetId: string) =>
    apiClient.get(`${ADS_API}/meta-adsets/${metaAdSetId}/detail`),

  // Ad insights with date range
  getMetaAdInsights: (metaAdId: string, params: any) =>
    apiClient.get(`${ADS_API}/meta-ads/${metaAdId}/insights`, { params }),

  // AI assistant
  aiAssistant: (data: any) => apiClient.post(`${ADS_API}/ai-assistant`, data),

  // Update Meta ad set (budget, schedule)
  updateMetaAdSetData: (metaAdSetId: string, data: any) =>
    apiClient.post(`${ADS_API}/meta-adsets/${metaAdSetId}`, data),

  // Update Meta campaign data
  updateMetaCampaignData: (metaCampaignId: string, data: any) =>
    apiClient.post(`${ADS_API}/meta-campaigns/${metaCampaignId}`, data),

  // Business & Funding
  getBusinesses: () => apiClient.get(`${ADS_API}/businesses`),
  createAdAccount: (data: any) => apiClient.post(`${ADS_API}/create-ad-account`, data),
  getFundingDetails: () => apiClient.get(`${ADS_API}/funding`),

  // Lead suppression
  getSuppressionStatus: () => apiClient.get(`${ADS_API}/setup/suppression/status`),
  enableSuppression: () => apiClient.post(`${ADS_API}/setup/suppression/enable`, {}),
  applySuppressionToActive: () =>
    apiClient.post(`${ADS_API}/setup/suppression/apply-to-active`, {}),
  backfillSuppression: () => apiClient.post(`${ADS_API}/setup/suppression/backfill`, {}),

  // Catalogs
  createProductCatalog: (data: { business_id: string; name: string; vertical?: string }) =>
    apiClient.post(`${ADS_API}/catalogs`, data),
  addProductToCatalog: (catalogId: string, product: any) =>
    apiClient.post(`${ADS_API}/catalogs/${catalogId}/products`, product),
  getProductCatalogs: () => apiClient.get(`${ADS_API}/catalogs`),
  getCatalogProductSets: (catalogId: string) =>
    apiClient.get(`${ADS_API}/catalogs/${catalogId}/product-sets`),
  getCatalogProducts: (catalogId: string) =>
    apiClient.get(`${ADS_API}/catalogs/${catalogId}/products`),

  // Meta Leads
  getLeadForms: () => apiClient.get(`${ADS_API}/leads/forms`),
  createLeadForm: (data: any) => apiClient.post(`${ADS_API}/leads/forms`, data),
  getLeadFormLeads: (formId: string, params?: any) =>
    apiClient.get(`${ADS_API}/leads/forms/${formId}/leads`, { params }),
  getAllPageLeads: (params?: any) => apiClient.get(`${ADS_API}/leads`, { params }),
  getLeadGenSubscriptionStatus: () => apiClient.get(`${ADS_API}/leads/subscription-status`),
  resubscribeLeadGenPages: () => apiClient.post(`${ADS_API}/leads/resubscribe`, {}),

  // Ad Account management
  getAgencies: () => apiClient.get(`${ADS_API}/setup/agencies`),
  getAdvertisableApplications: () => apiClient.get(`${ADS_API}/setup/advertisable-applications`),
  getAssignedUsers: () => apiClient.get(`${ADS_API}/setup/assigned-users`),
  updateAdAccount: (data: { spend_cap?: number; name?: string; notifications_enabled?: boolean }) =>
    apiClient.patch(`${ADS_API}/setup/account`, data),
  getReachFrequencyPredictions: () => apiClient.get(`${ADS_API}/setup/reach-frequency-predictions`),
  getConnectedInstagramAccounts: () => apiClient.get(`${ADS_API}/setup/connected-instagram-accounts`),

  // Ad copy & previews
  copyAd: (adId: string, data?: { status_option?: string }) =>
    apiClient.post(`${ADS_API}/meta-ads/${adId}/copy`, data ?? {}),
  getAdPreviews: (adId: string, params: { ad_format: string; creative_feature?: string }) =>
    apiClient.get(`${ADS_API}/meta-ads/${adId}/previews`, { params }),

  // Async insights
  createInsightsReport: (data: { object_id: string; level?: string; date_preset?: string; time_range?: any; breakdowns?: string; fields?: string }) =>
    apiClient.post(`${ADS_API}/insights/report`, data),
  pollInsightsReport: (reportRunId: string) =>
    apiClient.get(`${ADS_API}/insights/report/${reportRunId}`),

  // Targeting
  getBroadTargetingCategories: () => apiClient.get(`${ADS_API}/targeting/broad-categories`),

  // Pixels / Datasets
  getAdPixels: () => apiClient.get(`${ADS_API}/pixels`),
  createAdPixel: (data: { name: string }) => apiClient.post(`${ADS_API}/pixels`, data),

  // CAPI / conversions
  getCapiDatasets: () => apiClient.get(`${ADS_API}/capi/datasets`),
  getCustomConversions: () => apiClient.get(`${ADS_API}/capi/custom-conversions`),

  // Page / IG posts (for ad media picker)
  getPagePosts: (limit = 25) => apiClient.get(`${ADS_API}/page-posts`, { params: { limit } }),
  getInstagramMedia: (limit = 25) => apiClient.get(`${ADS_API}/instagram-media`, { params: { limit } }),

  // Video upload by URL
  uploadVideoByUrl: (data: { video_url: string; title?: string }) =>
    apiClient.post(`${ADS_API}/upload-video-url`, data),

  generateFromImage: async (file: File, organizationId?: string | null) => {
    const resolvedOrganizationId = organizationId || getOrganizationId();
    if (!resolvedOrganizationId) {
      throw new Error("Organization not found");
    }

    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    formData.append("image", file);
    formData.append("organization_id", resolvedOrganizationId);

    const response = await fetch(getAiAnalyticsUrl("/meta-ads/generate-from-image", "root"), {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw ApiError.fromResult(json, `HTTP ${response.status}`);
    }

    return json;
  },

  /**
   * AI campaign generation from a video clip.
   *
   * Accepts EITHER a File (multipart field `video`) OR a URL (multipart field
   * `video_url`). The backend extracts frames, runs GPT-4V on them, and
   * returns the same MetaAdCampaignSuggestionResponse shape as the image
   * variant — so callers can reuse the AiMetaAdsResponse handler.
   *
   * Limits (enforced server-side):
   *   - video ≤ 100 MB
   *   - duration ≤ 10 min
   *   - MIME starts with video/ (mp4 / mov / webm / mkv / avi / mpeg)
   *
   * Expect 30–60s latency depending on duration — frame extraction + LLM call.
   */
  generateFromVideo: async (
    input: { file?: File; videoUrl?: string },
    organizationId?: string | null
  ) => {
    const resolvedOrganizationId = organizationId || getOrganizationId();
    if (!resolvedOrganizationId) {
      throw new Error("Organization not found");
    }
    if (!input.file && !input.videoUrl) {
      throw new Error("Provide either a video file or a video_url");
    }

    const token = localStorage.getItem("auth_token");
    const formData = new FormData();
    if (input.file) {
      formData.append("video", input.file);
    } else if (input.videoUrl) {
      formData.append("video_url", input.videoUrl);
    }
    formData.append("organization_id", resolvedOrganizationId);

    const response = await fetch(getAiAnalyticsUrl("/meta-ads/generate-from-video", "root"), {
      method: "POST",
      // Explicitly DON'T set Content-Type — fetch must pick its own multipart
      // boundary. Setting it manually breaks the upload.
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const json = await response.json().catch(() => null);
    if (!response.ok) {
      throw ApiError.fromResult(json, `HTTP ${response.status}`);
    }
    return json;
  },
};

// === Hooks ===

export function useAdsSetupStatus() {
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();
  return useQuery({
    queryKey: ["ads", "setup-status", businessAccountId],
    queryFn: async () => {
      const res = await adsApi.getSetupStatus(businessAccountId);
      return res.data;
    },
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export function useLeadGenSubscriptionStatus(enabled = true) {
  return useQuery({
    queryKey: ["ads", "leadgen-subscription-status"],
    queryFn: async () => {
      const res = await adsApi.getLeadGenSubscriptionStatus();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export function useResubscribeLeadGenPages() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.resubscribeLeadGenPages();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "leadgen-subscription-status"] });
      const subscribed = data?.subscribed?.length ?? 0;
      const failed = data?.failed?.length ?? 0;
      toast({
        variant: failed > 0 ? "warning" : "success",
        title:
          failed > 0
            ? `Subscribed ${subscribed} page(s); ${failed} need reconnecting`
            : `Subscribed ${subscribed} page(s) to lead capture`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to subscribe pages" } });
    },
  });
}

export function useAdsBalance(enabled = true) {
  return useQuery({
    queryKey: ["ads", "balance"],
    queryFn: async () => {
      const res = await adsApi.getBalance();
      return res.data;
    },
    staleTime: 30 * 1000,
    enabled,
    retry: 0,
  });
}

export function useAdsCampaigns(
  params: {
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "campaigns", params],
    queryFn: async () => {
      const res = await adsApi.getCampaigns(params);
      return res.data;
    },
    staleTime: 30 * 1000,
    enabled,
    retry: 0,
  });
}

export function useAdsCampaign(id: string | null) {
  return useQuery({
    queryKey: ["ads", "campaign", id],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getCampaign(id);
      return res.data;
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useAdsCampaignInsights(
  id: string | null,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ["ads", "insights", id, startDate, endDate],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getCampaignInsights(id, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAdsLeadsChart(
  id: string | null,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ["ads", "leads-chart", id, startDate, endDate],
    queryFn: async () => {
      if (!id) return null;
      const res = await adsApi.getLeadsChart(id, {
        start_date: startDate,
        end_date: endDate,
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useAdsFlows() {
  return useQuery({
    queryKey: ["ads", "flows"],
    queryFn: async () => {
      const res = await adsApi.getFlows();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdsFetchedFromMeta(
  datePreset: string = "maximum",
  // Positional slot kept for existing callers that pass an account id. It is
  // NOT forwarded — this endpoint has always used the org's active account.
  // Declared so those call sites type-check rather than silently mismatching.
  _accountId?: string,
  range?: { since: string; until: string }
) {
  // A custom range is part of the cache key, or switching between two ranges
  // would serve the first one's data.
  const rangeKey = range?.since && range?.until ? `${range.since}:${range.until}` : null;
  return useQuery({
    queryKey: ["ads", "fetched-from-meta", datePreset, rangeKey],
    queryFn: async () => {
      const res = await adsApi.fetchAdsFromMeta(datePreset, range);
      return res.data;
    },
    staleTime: 60 * 1000,
    enabled: false, // Only fetch on demand
  });
}

export const BREAKDOWN_DIMENSIONS = [
  { key: "age_gender", label: "Age & Gender" },
  { key: "placement", label: "Placement" },
  { key: "region", label: "Region" },
  { key: "country", label: "Country" },
  { key: "device", label: "Device" },
  { key: "time_of_day", label: "Time of Day" },
] as const;

export type BreakdownDimensionKey = (typeof BREAKDOWN_DIMENSIONS)[number]["key"];

export interface BreakdownRow {
  bucket: string;
  keys: Record<string, string>;
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  frequency: number;
  cost_per_lead: number | null;
}

export interface BreakdownResult {
  dimension: BreakdownDimensionKey;
  label: string;
  rows: BreakdownRow[];
  totals: { spend: number; impressions: number; clicks: number; leads: number };
  date_preset: string;
  currency: string;
  ad_account_id: string;
}

/**
 * Breakdown insights for one dimension. Fetched on demand from Meta (live,
 * rate-limited) so it is disabled until `enabled` is true — the tab enables it
 * once mounted. Cached 10 min to avoid re-hitting Meta on every dimension flip
 * back-and-forth.
 */
export function useAdsBreakdown(
  dimension: BreakdownDimensionKey,
  datePreset: string = "last_30d",
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "breakdown", dimension, datePreset],
    queryFn: async (): Promise<BreakdownResult> => {
      const res = await adsApi.getBreakdown(dimension, datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load breakdown");
      }
      return res.data as BreakdownResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: any) => {
      // Don't retry a 400 (bad dimension / no account) — only transient errors.
      const status = err?.status ?? err?.statusCode;
      if (status === 400 || status === 401 || status === 403) return false;
      return count < 2;
    },
  });
}

export interface FunnelStep {
  key: string;
  label: string;
  count: number;
  pctOfTop: number | null;
  pctOfPrev: number | null;
  dropOffPct: number | null;
  costPer: number | null;
}

export interface FunnelResult {
  steps: FunnelStep[];
  spend: number;
  date_preset: string;
  currency: string;
  crm_buckets: { won: number; engaged: number; no_response: number; negative: number; open: number };
}

/**
 * Impressions → Clicks → Leads → Engaged → Won funnel. Live Meta account
 * insights + CRM buckets, cached 10 min. Disabled until `enabled` (the Overview
 * tab enables it once the account is connected).
 */
export function useAdsFunnel(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "funnel", datePreset],
    queryFn: async (): Promise<FunnelResult> => {
      const res = await adsApi.getFunnel(datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load funnel");
      }
      return res.data as FunnelResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: any) => {
      const status = err?.status ?? err?.statusCode;
      if (status === 400 || status === 401 || status === 403) return false;
      return count < 2;
    },
  });
}

// Report metric catalog — mirrors backend REPORT_METRICS (keep in sync).
export const REPORT_METRICS: Array<{ key: string; label: string; kind: string }> = [
  { key: "spend", label: "Spend", kind: "currency" },
  { key: "impressions", label: "Impressions", kind: "number" },
  { key: "reach", label: "Reach", kind: "number" },
  { key: "clicks", label: "Clicks", kind: "number" },
  { key: "leads", label: "Leads", kind: "number" },
  { key: "ctr", label: "CTR", kind: "percent" },
  { key: "cpc", label: "CPC", kind: "currency" },
  { key: "cpm", label: "CPM", kind: "currency" },
  { key: "frequency", label: "Frequency", kind: "number2" },
  { key: "cost_per_lead", label: "Cost / lead", kind: "currency" },
];
export const REPORT_LEVELS: Array<{ key: string; label: string }> = [
  { key: "campaign", label: "Campaigns" },
  { key: "adset", label: "Ad Sets" },
  { key: "ad", label: "Ads" },
];
export const DEFAULT_REPORT_METRICS = ["spend", "impressions", "clicks", "ctr", "leads", "cost_per_lead"];

export interface ReportRequest {
  level: string;
  metrics: string[];
  sortBy?: string;
  sortDir?: "asc" | "desc";
  date_preset?: string;
}
export interface ReportColumn { key: string; label: string; kind: string }
export interface ReportRow {
  id: string | null;
  name: string;
  status: string | null;
  values: Record<string, number | null>;
}
export interface ReportResult {
  level: string;
  metrics: string[];
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: Record<string, number | null>;
  date_preset: string;
  sortBy: string;
  sortDir: "asc" | "desc";
}

/**
 * Custom report. Keyed on the full request so changing metrics/level/sort
 * refetches. Disabled until `enabled` (the tab enables on first build click),
 * so the (relatively heavy) ads fetch only runs when the user asks for it.
 */
export function useAdsCustomReport(req: ReportRequest, enabled: boolean) {
  return useQuery({
    queryKey: ["ads", "custom-report", req],
    queryFn: async (): Promise<ReportResult> => {
      const res = await adsApi.buildCustomReport(req);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to build report");
      return res.data as ReportResult;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (count, err: any) => {
      const s = err?.status ?? err?.statusCode;
      if (s === 400 || s === 401 || s === 403) return false;
      return count < 2;
    },
  });
}

export interface WeeklyDigest {
  orgName: string;
  periodLabel: string;
  currency: string;
  current: { spend: number; leads: number; cost_per_lead: number };
  deltas: { spend: number | null; leads: number | null; cost_per_lead: number | null };
  topCreatives: Array<{ name: string; kind: string; spend: number; leads: number; cost_per_lead: number | null }>;
  funnel: Array<{ label: string; count: number; pctOfPrev: number | null }>;
  anomaly: { reason: string } | null;
  revenue: { revenue: number; revenuePerSpend: number | null; netProfit: number } | null;
}
export interface WeeklyDigestResult {
  digest: WeeklyDigest;
  html: string;
}

export function useAdsWeeklyDigest(enabled = true) {
  return useQuery({
    queryKey: ["ads", "weekly-digest"],
    queryFn: async (): Promise<WeeklyDigestResult> => {
      const res = await adsApi.getWeeklyDigest();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load weekly digest");
      return res.data as WeeklyDigestResult;
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    retry: (count, err: any) => {
      const s = err?.status ?? err?.statusCode;
      if (s === 400 || s === 401 || s === 403) return false;
      return count < 2;
    },
  });
}

export interface AnomalyResult {
  anomaly: boolean;
  kind: "cpl_spike" | "spend_spike" | null;
  latest: { date: string; spend: number; leads: number; cost_per_lead: number } | null;
  baselineCpl: number | null;
  baselineSpend: number | null;
  cplRatio: number | null;
  spendRatio: number | null;
  reason: string | null;
  ad_account_id: string;
  currency: string;
  history_days: number;
}

export function useAdsAnomalies(enabled = true) {
  return useQuery({
    queryKey: ["ads", "anomalies"],
    queryFn: async (): Promise<AnomalyResult> => {
      const res = await adsApi.getAnomalies();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load anomalies");
      return res.data as AnomalyResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
    retry: (count, err: any) => {
      const s = err?.status ?? err?.statusCode;
      if (s === 400 || s === 401 || s === 403) return false;
      return count < 2;
    },
  });
}

export interface CreativeRow {
  creative_key: string;
  creative_id: string | null;
  name: string;
  kind: "video" | "image" | "unknown";
  thumbnail: string | null;
  ad_count: number;
  spend: number;
  impressions: number;
  clicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cost_per_lead: number | null;
}
export interface VideoRow {
  ad_id: string | null;
  name: string;
  thumbnail: string | null;
  spend: number;
  plays: number;
  thruplays: number;
  p25: number; p50: number; p75: number; p100: number;
  rate25: number | null; rate50: number | null; rate75: number | null; rate100: number | null;
  thruplayRate: number | null;
}
export interface CreativeInsightsResult {
  creatives: CreativeRow[];
  videos: VideoRow[];
  date_preset: string;
  currency: string;
}

export function useAdsCreativeInsights(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "creative-insights", datePreset],
    queryFn: async (): Promise<CreativeInsightsResult> => {
      const res = await adsApi.getCreativeInsights(datePreset);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load creative insights");
      return res.data as CreativeInsightsResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: any) => {
      const s = err?.status ?? err?.statusCode;
      if (s === 400 || s === 401 || s === 403) return false;
      return count < 2;
    },
  });
}

export interface PacingAdSet {
  id: string | null;
  name: string | null;
  dailyBudget: number;
  spentToday: number;
  pctOfBudget: number | null;
  alert: "overspend" | "on_track" | "underspend" | "no_budget";
}
export interface PacingResult {
  adsets: PacingAdSet[];
  counts: { overspend: number; on_track: number; underspend: number; no_budget: number };
  projection: {
    spendThisMonth: number;
    dayOfMonth: number;
    daysInMonth: number;
    avgPerDay: number;
    projectedMonthEnd: number;
  };
  currency: string;
}

export function useAdsSpendPacing(enabled = true) {
  return useQuery({
    queryKey: ["ads", "pacing"],
    queryFn: async (): Promise<PacingResult> => {
      const res = await adsApi.getSpendPacing();
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load pacing");
      return res.data as PacingResult;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (count, err: any) => {
      const s = err?.status ?? err?.statusCode;
      if (s === 400 || s === 401 || s === 403) return false;
      return count < 2;
    },
  });
}

export interface RevenuePerLeadResult {
  leads: number;
  sales: number;
  revenue: number;
  spend: number;
  revenuePerLead: number | null;
  revenuePerSale: number | null;
  revenuePerSpend: number | null;
  costPerLead: number | null;
  costPerSale: number | null;
  netProfit: number;
  profitable: boolean;
  date_preset: string;
  currency: string;
}

export function useAdsRevenuePerLead(datePreset: string = "last_30d", enabled = true) {
  return useQuery({
    queryKey: ["ads", "revenue-per-lead", datePreset],
    queryFn: async (): Promise<RevenuePerLeadResult> => {
      const res = await adsApi.getRevenuePerLead(datePreset);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to load revenue per lead");
      return res.data as RevenuePerLeadResult;
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: (count, err: any) => {
      const s = err?.status ?? err?.statusCode;
      if (s === 400 || s === 401 || s === 403) return false;
      return count < 2;
    },
  });
}

export interface MultiAccountSummary {
  accounts: Array<{
    ad_account_id: string;
    ad_account_name: string;
    currency: string;
    spend: number;
    leads: number;
    cost_per_lead: number;
    error: string | null;
  }>;
  totals: {
    account_count: number;
    date_preset: string;
    total_spend: number;
    total_leads: number;
    cost_per_lead: number;
    currency: string;
  };
}

export function useMultiAccountSummary() {
  return useMutation({
    mutationFn: async (
      vars: { adAccountIds: string[]; datePreset?: string }
    ): Promise<MultiAccountSummary> => {
      const res = await adsApi.getMultiAccountSummary(vars.adAccountIds, vars.datePreset);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load multi-account summary");
      }
      return res.data as MultiAccountSummary;
    },
  });
}

export function useProductCatalogs() {
  return useQuery({
    queryKey: ["ads", "catalogs"],
    queryFn: async () => {
      const res = await adsApi.getProductCatalogs();
      return res.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogProductSets(catalogId: string | null) {
  return useQuery({
    queryKey: ["ads", "catalog-product-sets", catalogId],
    queryFn: async () => {
      if (!catalogId) return [];
      const res = await adsApi.getCatalogProductSets(catalogId);
      return res.data || [];
    },
    enabled: !!catalogId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogProducts(catalogId: string | null) {
  return useQuery({
    queryKey: ["ads", "catalog-products", catalogId],
    queryFn: async () => {
      if (!catalogId) return [];
      const res = await adsApi.getCatalogProducts(catalogId);
      return res.data || [];
    },
    enabled: !!catalogId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateProductCatalog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { business_id: string; name: string; vertical?: string }) => {
      const res = await adsApi.createProductCatalog(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "catalogs"] });
      toast({ variant: "success", title: "Catalog created successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create catalog" } });
    },
  });
}

export function useAddProductToCatalog() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ catalogId, product }: { catalogId: string; product: any }) => {
      const res = await adsApi.addProductToCatalog(catalogId, product);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "catalog-products", variables.catalogId] });
      toast({ variant: "success", title: "Product added to catalog" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to add product" } });
    },
  });
}

export function useGenerateMetaAdsFromImage() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ file, organizationId }: { file: File; organizationId?: string | null }) => {
      return adsApi.generateFromImage(file, organizationId);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "AI ad generation failed" } });
    },
  });
}

export function useGenerateMetaAdsFromVideo() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      file,
      videoUrl,
      organizationId,
    }: {
      file?: File;
      videoUrl?: string;
      organizationId?: string | null;
    }) => {
      return adsApi.generateFromVideo({ file, videoUrl }, organizationId);
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "AI ad generation failed" } });
    },
  });
}

// === Mutations ===

export function useAdsSetupConnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.connectAdAccount({
        ...data,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      toast({ variant: "success", title: "Ad account connected successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to connect ad account" } });
    },
  });
}

/**
 * Meta's inventory for the connected Facebook user.
 *
 * `includeBusinessScoped` also walks every Business the user administers, which is
 * the ONLY way ad accounts granted through Business Manager appear (/me/adaccounts
 * returns just directly-assigned ones). It costs 2 + 2N Meta calls, so it is part
 * of the query KEY — the cheap page-load read and the thorough picker read are
 * genuinely different queries and must not share a cache entry.
 *
 * THROWS on failure. It used to `return res.data` without checking `res.error`, so
 * a failed Meta call resolved to `undefined` with `isError === false` — every
 * consumer then read `data?.ad_accounts || []` and confidently rendered "every
 * account is already connected" when in truth nothing had loaded. Callers can now
 * tell the two apart.
 */
export function useAvailableAdAccounts(
  enabled = false,
  opts?: { includeBusinessScoped?: boolean }
) {
  const includeBusinessScoped = !!opts?.includeBusinessScoped;
  return useQuery({
    queryKey: ["ads", "available-ad-accounts", { includeBusinessScoped }],
    queryFn: async () => {
      const res = await adsApi.getAvailableAdAccounts({ includeBusinessScoped });
      if (res.error) throw ApiError.fromResult(res, "Failed to load ad accounts from Facebook");
      return res.data;
    },
    enabled,
    staleTime: 60 * 1000,
    // A 4xx here is a settled answer ("no ad account connected"), not a blip. The
    // global default reads `error.status`, which ApiError does not set, so without
    // this a 400 would be retried twice for nothing.
    retry: (failureCount, error: unknown) => {
      const status = Number((error as { statusCode?: number } | null)?.statusCode);
      if (status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}

export function useSwitchAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.switchAdAccount({
        ...data,
        ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
      });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      // The meta-ads dashboards, setup status, and balance all live under the
      // ["ads", ...] prefix, but the Instagram-only ads dashboard uses
      // ["instagram-ads", ...]. Invalidating both keeps every surface in sync
      // after the user swaps ad accounts — otherwise the old account's ads keep
      // showing because React Query matches by prefix.
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      queryClient.invalidateQueries({ queryKey: ["instagram-ads"] });
      toast({ variant: "success", title: "Ad account switched successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to switch ad account" } });
    },
  });
}

export function useAdsDisconnect() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { selectedAccountId: businessAccountId } = useBusinessAccountContext();

  return useMutation({
    // Pass an ad account id to disconnect THAT account; omit to disconnect the
    // active one. `mutate()` with no argument still works, so existing callers are
    // unaffected.
    mutationFn: async (adAccountId?: string) => {
      const res = await adsApi.disconnect(adAccountId, businessAccountId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (data: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      // When the org still holds another ad account the backend promotes it, so the
      // workspace stays connected. Naming it matters: campaigns, insights and
      // conversion tracking all follow the active account, and swapping that
      // silently would leave the user looking at a different account's numbers
      // without being told.
      const r = data as {
        promoted_ad_account_name?: string;
        promoted_ad_account_id?: string;
        disconnected_ad_account_name?: string;
        disconnected_ad_account_id?: string;
        already_disconnected?: boolean;
      } | null;
      const promotedLabel = r?.promoted_ad_account_name || r?.promoted_ad_account_id;
      const removedLabel = r?.disconnected_ad_account_name || r?.disconnected_ad_account_id;

      if (r?.already_disconnected) {
        // Idempotent path — say so rather than claiming a fresh disconnect.
        toast({
          variant: "success",
          title: removedLabel ? `${removedLabel} was already disconnected` : "Already disconnected",
        });
        return;
      }

      toast({
        variant: "success",
        title: removedLabel ? `Disconnected ${removedLabel}` : "Ad account disconnected",
        ...(promotedLabel ? { description: `Now using ${promotedLabel}.` } : {}),
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to disconnect" } });
    },
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createCampaign(data);
      if (res.error) throw ApiError.fromResult(res);
      return { data: res.data, warning: res.warning };
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      if (result?.warning) {
        toast({ title: "Campaign saved as draft", description: result.warning, variant: "default" });
      } else {
        toast({ variant: "success", title: "Campaign created successfully" });
      }
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create campaign" } });
    },
  });
}

export function useUpdateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      adsApi.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["ads", "campaign"] });
      toast({ variant: "success", title: "Campaign updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update campaign" } });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success", title: "Campaign deleted" });
    },
  });
}

export function useSyncCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.syncCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads"] });
      toast({ variant: "success", title: "Campaign synced from Meta" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Sync failed" } });
    },
  });
}

export function useDuplicateCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => adsApi.duplicateCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success", title: "Campaign duplicated" });
    },
  });
}

export function useSearchInterests() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchInterests(query),
  });
}

export function useSearchLocations() {
  return useMutation({
    mutationFn: (query: string) => adsApi.searchLocations(query),
  });
}

export function useOAuthCallback() {
  return useMutation({
    mutationFn: (code: string) => adsApi.handleCallback(code),
  });
}

export function useGetOAuthUrl() {
  return useMutation({
    mutationFn: () => adsApi.getOAuthUrl(),
  });
}

export function useUploadAdImage() {
  return useMutation({
    mutationFn: async (imageUrl: string) => {
      const res = await adsApi.uploadImage(imageUrl);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUpdateMetaAd() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ metaAdId, data }: { metaAdId: string; data: any }) => {
      const res = await adsApi.updateMetaAd(metaAdId, data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Ad updated successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad" } });
    },
  });
}

export function useUpdateMetaCampaign() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ metaCampaignId, status }: { metaCampaignId: string; status: string }) => {
      const res = await adsApi.updateMetaCampaign(metaCampaignId, { status });
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Campaign status updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update campaign" } });
    },
  });
}

export function useUpdateMetaAdSet() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      metaAdSetId,
      status,
      name,
      daily_budget,
    }: {
      metaAdSetId: string;
      status?: string;
      name?: string;
      daily_budget?: number;
    }) => {
      const payload: Record<string, any> = {};
      if (status !== undefined) payload.status = status;
      if (name !== undefined) payload.name = name;
      if (daily_budget !== undefined) payload.daily_budget = daily_budget;
      const res = await adsApi.updateMetaAdSet(metaAdSetId, payload);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      const what =
        variables.name !== undefined
          ? "Ad Set name updated"
          : variables.daily_budget !== undefined
            ? "Ad Set budget updated"
            : "Ad Set status updated";
      toast({ title: what });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad set" } });
    },
  });
}

export function useGenerateAdCopy() {
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.generateAdCopy(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

// === LEAD SUPPRESSION ===

export function useSuppressionStatus() {
  return useQuery({
    queryKey: ["ads", "suppression-status"],
    queryFn: async () => {
      const res = await adsApi.getSuppressionStatus();
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useEnableSuppression() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.enableSuppression();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to enable suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Lead suppression enabled",
        description: `Applied to ${data?.updated ?? 0} ad set${data?.updated === 1 ? "" : "s"} (${data?.alreadyExcluded ?? 0} already had it).`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to enable suppression" } });
    },
  });
}

export function useApplySuppressionToActive() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.applySuppressionToActive();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to apply suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Suppression re-applied",
        description: `${data?.updated ?? 0} updated, ${data?.alreadyExcluded ?? 0} already had it, ${data?.failures?.length ?? 0} failed.`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to apply suppression" } });
    },
  });
}

export function useBackfillSuppression() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await adsApi.backfillSuppression();
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to backfill suppression");
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suppression-status"] });
      toast({ variant: "success",
        title: "Backfill complete",
        description: `Uploaded ${data?.uploaded ?? 0} contacts in ${data?.batches ?? 0} batch${data?.batches === 1 ? "" : "es"}.`,
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Backfill failed" } });
    },
  });
}

export function useUploadAdImageFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await adsApi.uploadImageFile(formData);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useUploadAdVideoFile() {
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await adsApi.uploadVideoFile(formData);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

export function useMetaAdDetail(metaAdId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-ad-detail", metaAdId],
    queryFn: async () => {
      if (!metaAdId) return null;
      const res = await adsApi.getMetaAdDetail(metaAdId);
      return res.data;
    },
    enabled: !!metaAdId,
    staleTime: 30 * 1000,
  });
}

// === DRILL-DOWN HOOKS ===

export function useMetaCampaignDetail(metaCampaignId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-campaign-detail", metaCampaignId],
    queryFn: async () => {
      if (!metaCampaignId) return null;
      const res = await adsApi.getMetaCampaignDetail(metaCampaignId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaCampaignId,
    staleTime: 30 * 1000,
    retry: 2,
  });
}

export function useMetaAdSetDetail(metaAdSetId: string | null) {
  return useQuery({
    queryKey: ["ads", "meta-adset-detail", metaAdSetId],
    queryFn: async () => {
      if (!metaAdSetId) return null;
      const res = await adsApi.getMetaAdSetDetail(metaAdSetId);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    enabled: !!metaAdSetId,
    staleTime: 30 * 1000,
    retry: 2,
  });
}

export function useMetaAdInsights(
  metaAdId: string | null,
  params: { start_date?: string; end_date?: string; date_preset?: string } = {}
) {
  return useQuery({
    queryKey: ["ads", "meta-ad-insights", metaAdId, params],
    queryFn: async () => {
      if (!metaAdId) return null;
      const res = await adsApi.getMetaAdInsights(metaAdId, params);
      return res.data;
    },
    enabled: !!metaAdId,
    staleTime: 60 * 1000,
  });
}

export function useAiAssistant() {
  return useMutation({
    mutationFn: async (data: { question: string; context: any }) => {
      const res = await adsApi.aiAssistant(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
  });
}

// === BUSINESS & FUNDING ===

export function useBusinesses(enabled = false) {
  return useQuery({
    queryKey: ["ads", "businesses"],
    queryFn: async () => {
      const res = await adsApi.getBusinesses();
      return res.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createAdAccount(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "available-ad-accounts"] });
      toast({ variant: "success", title: "Ad account created successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create ad account" } });
    },
  });
}

export function useFundingDetails(enabled = false) {
  return useQuery({
    queryKey: ["ads", "funding-details"],
    queryFn: async () => {
      const res = await adsApi.getFundingDetails();
      return res.data;
    },
    enabled,
    staleTime: 30 * 1000,
  });
}

// === META LEADS ===

export function useLeadForms() {
  return useQuery({
    queryKey: ["ads", "lead-forms"],
    queryFn: async () => {
      const res = await adsApi.getLeadForms();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateLeadForm() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await adsApi.createLeadForm(data);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to create lead form");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "lead-forms"] });
      toast({ variant: "success", title: "Lead form created", description: "Your Instant Form has been created on Meta." });
    },
    onError: (err: any) => {
      showApiError(err, { fallback: { title: "Failed to create form" } });
    },
  });
}

export function useLeadFormLeads(formId: string | null, limit = 50) {
  return useQuery({
    queryKey: ["ads", "lead-form-leads", formId, limit],
    queryFn: async () => {
      if (!formId) return null;
      const res = await adsApi.getLeadFormLeads(formId, { limit });
      return res.data;
    },
    enabled: !!formId,
    staleTime: 60 * 1000,
  });
}

export function useAllPageLeads(limit = 100, offset = 0) {
  return useQuery({
    queryKey: ["ads", "all-page-leads", limit, offset],
    queryFn: async () => {
      const res = await adsApi.getAllPageLeads({ limit, offset });
      return res.data;
    },
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useAdsAudiences() {
  return useQuery({
    queryKey: ["ads", "audiences"],
    queryFn: async () => {
      const res = await adsApi.getAudiences();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useAdsAudiencePresets() {
  return useQuery({
    queryKey: ["ads", "audience-presets"],
    queryFn: async () => {
      const res = await adsApi.getAudiencePresets();
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

// === AD ACCOUNT MANAGEMENT ===

export function useAdAgencies(enabled = false) {
  return useQuery({
    queryKey: ["ads", "agencies"],
    queryFn: async () => {
      const res = await adsApi.getAgencies();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdvertisableApplications(enabled = false) {
  return useQuery({
    queryKey: ["ads", "advertisable-applications"],
    queryFn: async () => {
      const res = await adsApi.getAdvertisableApplications();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAssignedUsers(enabled = false) {
  return useQuery({
    queryKey: ["ads", "assigned-users"],
    queryFn: async () => {
      const res = await adsApi.getAssignedUsers();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateAdAccount() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { spend_cap?: number; name?: string; notifications_enabled?: boolean }) => {
      const res = await adsApi.updateAdAccount(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "setup-status"] });
      toast({ variant: "success", title: "Ad account updated" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to update ad account" } });
    },
  });
}

export function useReachFrequencyPredictions(enabled = false) {
  return useQuery({
    queryKey: ["ads", "reach-frequency-predictions"],
    queryFn: async () => {
      const res = await adsApi.getReachFrequencyPredictions();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConnectedInstagramAccounts(enabled = true) {
  return useQuery({
    queryKey: ["ads", "connected-instagram-accounts"],
    queryFn: async () => {
      const res = await adsApi.getConnectedInstagramAccounts();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// === AD COPY & PREVIEWS ===

export function useCopyAd() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ adId, statusOption }: { adId: string; statusOption?: string }) => {
      const res = await adsApi.copyAd(adId, statusOption ? { status_option: statusOption } : undefined);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "fetched-from-meta"] });
      toast({ variant: "success", title: "Ad duplicated successfully" });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to duplicate ad" } });
    },
  });
}

export function useAdPreviews(
  adId: string | null,
  adFormat: string,
  creativeFeature?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ["ads", "ad-previews", adId, adFormat, creativeFeature],
    queryFn: async () => {
      if (!adId || !adFormat) return null;
      const res = await adsApi.getAdPreviews(adId, {
        ad_format: adFormat,
        ...(creativeFeature ? { creative_feature: creativeFeature } : {}),
      });
      return res.data;
    },
    enabled: !!adId && !!adFormat && enabled,
    staleTime: 60 * 1000,
  });
}

// === ASYNC INSIGHTS ===

export function useCreateInsightsReport() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      object_id: string;
      level?: string;
      date_preset?: string;
      time_range?: any;
      breakdowns?: string;
      fields?: string;
    }) => {
      const res = await adsApi.createInsightsReport(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Failed to create insights report" } });
    },
  });
}

export function useInsightsReport(reportRunId: string | null) {
  return useQuery({
    queryKey: ["ads", "insights-report", reportRunId],
    queryFn: async () => {
      if (!reportRunId) return null;
      const res = await adsApi.pollInsightsReport(reportRunId);
      return res.data;
    },
    enabled: !!reportRunId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed") return false;
      return 3000;
    },
    staleTime: 0,
  });
}

// === TARGETING ===

export function useBroadTargetingCategories(enabled = false) {
  return useQuery({
    queryKey: ["ads", "broad-targeting-categories"],
    queryFn: async () => {
      const res = await adsApi.getBroadTargetingCategories();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

// === PIXELS / DATASETS ===

export function useAdPixels(enabled = false) {
  return useQuery({
    queryKey: ["ads", "pixels"],
    queryFn: async () => {
      const res = await adsApi.getAdPixels();
      // apiClient unwraps { success, data } — res.data is already the array
      return res.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateAdPixel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const res = await adsApi.createAdPixel(data);
      if (res.error || res.success === false) throw ApiError.fromResult(res, "Failed to create pixel");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "pixels"] });
    },
  });
}

// === CAPI / CONVERSIONS ===

export function useCapiDatasets(enabled = false) {
  return useQuery({
    queryKey: ["ads", "capi-datasets"],
    queryFn: async () => {
      const res = await adsApi.getCapiDatasets();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCustomConversions(enabled = false) {
  return useQuery({
    queryKey: ["ads", "custom-conversions"],
    queryFn: async () => {
      const res = await adsApi.getCustomConversions();
      return res.data?.data || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// === PAGE / IG POSTS (media picker) ===

export function usePagePosts(enabled = false) {
  return useQuery({
    queryKey: ["ads", "page-posts"],
    queryFn: async () => {
      const res = await adsApi.getPagePosts(25);
      const payload = res.data;
      // New shape: { pages, posts, warning }. Tolerate the legacy flat-array
      // shape (a server that hasn't been redeployed yet) so the UI never breaks.
      if (Array.isArray(payload)) {
        return { pages: [], posts: payload, warning: null };
      }
      return {
        pages: payload?.pages || [],
        posts: payload?.posts || [],
        warning: payload?.warning || null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInstagramMedia(enabled = false) {
  return useQuery({
    queryKey: ["ads", "instagram-media"],
    queryFn: async () => {
      const res = await adsApi.getInstagramMedia(25);
      const payload = res.data;
      // New shape: { accounts, media, warning }. Tolerate the legacy flat-array
      // shape (a server that hasn't been redeployed yet) so the UI never breaks.
      if (Array.isArray(payload)) {
        return { accounts: [], media: payload, warning: null };
      }
      return {
        accounts: payload?.accounts || [],
        media: payload?.media || [],
        warning: payload?.warning || null,
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// === VIDEO UPLOAD BY URL ===

export function useUploadVideoByUrl() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { video_url: string; title?: string }) => {
      const res = await adsApi.uploadVideoByUrl(data);
      if (res.error) throw ApiError.fromResult(res);
      return res.data;
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Video upload failed" } });
    },
  });
}

// === RUN AS AD (T1.3) ===

export interface AdRecommendation {
  should_promote: boolean;
  multiplier?: number;
  metric?: string;
  post_stats?: {
    reach?: number;
    engagement?: number;
    like_count?: number;
    comment_count?: number;
    share_count?: number;
    saved?: number;
    engagement_rate?: number;
  };
  recommended_objective?: string;
  recommended_budget?: number;
  estimated_leads_low?: number;
  estimated_leads_high?: number;
  cost_per_lead_low?: number;
  cost_per_lead_high?: number;
  target_audience?: {
    age_range?: string;
    gender?: string;
    interests?: string[];
    location_type?: string;
  };
  reason?: string;
  token_usage?: {
    model?: string;
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
    estimated_cost_usd?: number;
    total_cost_inr?: number;
    charged_cost_inr?: number;
  };
}

export function useAdRecommendation() {
  return useMutation({
    mutationFn: async (data: {
      post?: Record<string, any>;
      baseline?: Record<string, any>;
      image_url?: string;
      business_account_id?: string;
    }): Promise<AdRecommendation> => {
      const baseUrl = getAiAnalyticsBaseUrl('root');
      const orgId = getOrganizationId();
      const resp = await fetch(`${baseUrl}/meta-ads/suggest-from-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post: data.post || {},
          baseline: data.baseline || null,
          business_account_id: data.business_account_id || null,
          organization_id: orgId || null,
        }),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw ApiError.fromResult(json, 'Failed to get recommendation');
      }
      return json as AdRecommendation;
    },
  });
}

export function useCreateAdFromPost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: {
      source: { page_post_id?: string; ig_media_id?: string };
      name?: string;
      objective?: string;
      destination?: string;
      lead_form_id?: string;
      daily_budget?: number;
      targeting_spec?: any;
      business_account_id?: string;
    }) => {
      const res = await adsApi.createAdFromPost(data);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to create ad from post");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "campaigns"] });
      toast({ variant: "success",
        title: "Ad created (paused)",
        description: "Review it in the Ads page, then activate to start delivery.",
      });
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Couldn't create the ad" } });
    },
  });
}

// === REVENUE ATTRIBUTION (T2.4) ===

export interface AttributionRow {
  meta_ad_id: string | null;
  ad_name: string | null;
  leads: number;
  meetings: number;
  sales: number;
  revenue: number;
  currency: string;
  source: string;
}

export function useAdAttribution(params: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", "attribution", params],
    queryFn: async (): Promise<AttributionRow[]> => {
      const res = await adsApi.getAttribution(params);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to load attribution");
      }
      return (res.data as AttributionRow[]) || [];
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export type { CrmLeadQualityRow } from "@/lib/ads/leadQuality";

export function useAdsLeadQuality(params: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", "lead-quality", params],
    queryFn: async (): Promise<import("@/lib/ads/leadQuality").CrmLeadQualityRow[]> => {
      // Soft-fail: never throw — Ads tab must keep working when CRM quality
      // is unavailable (auth blip, 5xx). Empty array → CRM columns show "—".
      try {
        const res = await adsApi.getLeadQuality(params);
        if (res.error || res.success === false) return [];
        return (res.data as import("@/lib/ads/leadQuality").CrmLeadQualityRow[]) || [];
      } catch {
        return [];
      }
    },
    enabled,
    staleTime: 60 * 1000,
    retry: 0,
  });
}

export type CrmLeadStageLead = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  type: string | null;
  created_at: string | null;
};

export type CrmLeadStageBucket = {
  stage_id: string | null;
  stage_name: string;
  stage_color: string | null;
  board_name: string | null;
  count: number;
  is_unassigned: boolean;
  leads?: CrmLeadStageLead[];
  leads_truncated?: boolean;
};

export type CrmLeadStageBreakdown = {
  ad_id: string | null;
  ad_name: string | null;
  match_key: string;
  crm_leads: number;
  stages: CrmLeadStageBucket[];
};

/** Lazy-load per-stage CRM counts when the Ads-tab drawer opens. Soft-fails to null. */
export function useAdsLeadQualityStages(
  params: { adId?: string | null; adName?: string | null; from?: string; to?: string },
  enabled = false
) {
  return useQuery({
    queryKey: ["ads", "lead-quality-stages", params],
    queryFn: async (): Promise<CrmLeadStageBreakdown | null> => {
      try {
        const res = await adsApi.getLeadQualityStages({
          ad_id: params.adId || undefined,
          ad_name: params.adName || undefined,
          from: params.from,
          to: params.to,
        });
        if (res.error || res.success === false) return null;
        return (res.data as CrmLeadStageBreakdown) || null;
      } catch {
        return null;
      }
    },
    enabled:
      enabled &&
      !!(
        (params.adId && String(params.adId).trim()) ||
        (params.adName && String(params.adName).trim())
      ),
    staleTime: 60 * 1000,
    retry: 0,
  });
}

// === AI SUGGEST AD (Phase 3) ===
// On-demand ranking: fetches the org's recent IG posts, calls the we-next-ai
// /meta-ads/rank-posts microservice directly from the frontend, and returns
// ranked promote suggestions. No cron dependency.

export interface AdSuggestion {
  id: string;
  ig_media_id: string | null;
  page_post_id: string | null;
  caption: string;
  thumbnail_url: string | null;
  media_url?: string | null;
  like_count?: number;
  comment_count?: number;
  multiplier: number; // e.g. 4.2 → "4.2x better than average"
  metric: string; // what's outperforming, e.g. "engagement", "reach"
  recommended_objective: string;
  recommended_budget: number; // rupees
  estimated_leads_low: number;
  estimated_leads_high: number;
  reason?: string;
  status: "pending" | "dismissed" | "promoted";
}

export interface PostPromoteSuggestion {
  should_promote: boolean;
  multiplier?: number;
  metric?: string;
  recommended_objective?: string;
  recommended_budget?: number;
  estimated_leads_low?: number;
  estimated_leads_high?: number;
  reason: string;
  ai_source?: string;
}

/**
 * "Should I promote this post?" — calls the real AI service via the wenext
 * backend (T3.3). Fails open server-side, so a resolved result with
 * should_promote:false is the worst case (never a thrown error path here).
 */
export function useSuggestFromPost() {
  return useMutation({
    mutationFn: async (vars: {
      post: Record<string, any>;
      baseline?: Record<string, any>;
      business_account_id?: string;
    }): Promise<PostPromoteSuggestion> => {
      const res = await adsApi.suggestFromPost(vars);
      if (res.error || res.success === false) {
        // Defensive — backend fails open, but never throw a broken UI on a blip.
        return { should_promote: false, reason: "Suggestion unavailable right now.", ai_source: "unavailable" };
      }
      return res.data as PostPromoteSuggestion;
    },
  });
}

/**
 * On-demand rank-posts: fetches the org's IG accounts → their recent posts →
 * calls the we-next-ai /meta-ads/rank-posts microservice directly.
 * Returns only posts the AI says are worth promoting.
 */
export function useAdSuggestions(enabled = true) {
  return useQuery({
    queryKey: ["ads", "suggestions-live"],
    queryFn: async (): Promise<AdSuggestion[]> => {
      // 1. Fetch the org's connected Instagram accounts
      const accountsRes = await apiClient.get('/instagram/accounts');
      const accounts: any[] = accountsRes?.data || [];
      if (accounts.length === 0) return [];

      // 2. Fetch recent posts for each account (parallel)
      const allPosts: any[] = [];
      await Promise.all(
        accounts.map(async (acc: any) => {
          try {
            const res = await apiClient.get('/instagram/automation/posts', {
              params: { instagram_account_id: acc.id, limit: 25 },
            });
            const posts: any[] = res?.data || [];
            allPosts.push(...posts);
          } catch {
            // skip accounts that fail
          }
        })
      );

      if (allPosts.length < 3) return []; // not enough posts for a meaningful baseline

      // 3. Compute baseline
      const engagements = allPosts.map((p: any) => (Number(p.like_count || 0) + Number(p.comment_count || 0)));
      const avgEngagement = engagements.reduce((a: number, b: number) => a + b, 0) / engagements.length;

      // 4. Build the rank-posts payload
      const payloadPosts = allPosts.map((p: any) => ({
        ig_media_id: p.ig_post_id || p.id,
        caption: p.caption || "",
        thumbnail_url: p.thumbnail_url || p.media_url || null,
        engagement: Number(p.like_count || 0) + Number(p.comment_count || 0),
        like_count: Number(p.like_count || 0),
        comment_count: Number(p.comment_count || 0),
      }));

      // 5. Call the microservice directly
      const baseUrl = getAiAnalyticsBaseUrl('root');
      const orgId = getOrganizationId();
      const resp = await fetch(`${baseUrl}/meta-ads/rank-posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          posts: payloadPosts,
          baseline: { avg_engagement: Math.round(avgEngagement * 100) / 100 },
          organization_id: orgId || null,
        }),
      });

      if (!resp.ok) return [];
      const json = await resp.json();
      const suggestions: any[] = json?.suggestions || [];

      // 6. Map back to AdSuggestion shape, keeping only promote-worthy
      return suggestions
        .filter((s: any) => s.should_promote)
        .map((s: any, idx: number) => {
          const post = allPosts[s.index ?? idx];
          return {
            id: post?.ig_post_id || post?.id || `suggestion-${idx}`,
            ig_media_id: post?.ig_post_id || post?.id || null,
            page_post_id: null,
            caption: post?.caption || "",
            thumbnail_url: post?.thumbnail_url || post?.media_url || null,
            media_url: post?.media_url || null,
            like_count: Number(post?.like_count || 0),
            comment_count: Number(post?.comment_count || 0),
            multiplier: Number(s.multiplier || 1),
            metric: s.metric || "engagement",
            recommended_objective: s.recommended_objective || "OUTCOME_LEADS",
            recommended_budget: Number(s.recommended_budget || 500),
            estimated_leads_low: Number(s.estimated_leads_low || 0),
            estimated_leads_high: Number(s.estimated_leads_high || 0),
            reason: s.reason || "",
            status: "pending" as const,
          };
        });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useDismissAdSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await adsApi.dismissSuggestion(id);
      if (res.error || res.success === false) {
        throw ApiError.fromResult(res, "Failed to dismiss suggestion");
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ads", "suggestions"] });
    },
  });
}

export { adsApi };
